import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsyncPipe, NgFor } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PillItem } from '../../../models/shared/pill-item';
import { Subscribable } from '../../../models/base/subscribable';
import { PillComponent } from '../../../ui/pills/pill/pill.component';

@Component({
  selector: 'app-search-pill-input',
  templateUrl: './search-pill-input.component.html',
  styleUrls: ['./search-pill-input.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgFor, PillComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchPillInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class SearchPillInputComponent extends Subscribable implements ControlValueAccessor {
  @Input() placeholder: string = 'Search...';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Input() maxPills: number = 1;
  @Output() valueChange = new EventEmitter<string[] | null>();

  @ViewChild('input') input!: ElementRef;

  public value: string[] | null = null;
  public disabled: boolean = false;

  private onChange: (value: string[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  private _selectedValues = new BehaviorSubject<string[] | null>([]);
  public selectedValues$ = this._selectedValues as Observable<string[] | null>;

  public selectedPillItems$ = this.selectedValues$.pipe(
    filter((val): val is string[] => val !== null),
    map(values => values.map(v => new PillItem(v, true)))
  );

  private listenToSelectedValues = this.selectedValues$.subscribeWhileAlive({
    owner: this,
    next: values => {
      if (this.input?.nativeElement) {
        const shouldDisable = !!values && values.length >= this.maxPills;
        this.input.nativeElement.disabled = shouldDisable;
        this.disabled = shouldDisable;
      }
    }
  });

  public writeValue(value: string[] | null): void {
    const newValue = value ?? [];
    this._selectedValues.next(newValue);

    if (this.input?.nativeElement) {
      this.input.nativeElement.disabled = newValue.length >= this.maxPills;
      this.disabled = this.input.nativeElement.disabled;
    }
  }

  public registerOnChange(fn: (value: string[] | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public handleInput(event: Event): void {
    // Only handle input on Enter key
  }

  public handleEnter(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();
    if (event.key === 'Enter' && value) {
      this.selectedValues$.once(values => {
        if (values && values.length < this.maxPills) {
          const updated = [...values, value];
          this._selectedValues.next(updated);
          this.valueChange.emit(updated);
          this.onChange(updated);
          target.value = '';
        }
      });
    }
  }

  public handlePillRemoved(removedPill: PillItem): void {
    this.selectedValues$.once(values => {
      if (values) {
        const updatedValues = values.filter(v => v !== removedPill.text);
        this._selectedValues.next(updatedValues);
        this.valueChange.emit(updatedValues);
        this.onChange(updatedValues);
      }
    });
  }
}
